import type { JSX } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WIZARD_STEPS } from '../types/candidate-wizard.types';
import type { WizardStepDefinition, WizardStepId } from '../types/candidate-wizard.types';
import type { WizardCompletion } from '../utils/wizard-progress';

type StepState = 'complete' | 'active' | 'todo';

interface CandidateWizardStepperProps {
  activeStepId: WizardStepId;
  completion: WizardCompletion;
  onSelect: (stepId: WizardStepId) => void;
}

const MARKER_STYLES: Record<StepState, string> = {
  complete: 'bg-green-600 text-white',
  active: 'bg-primary text-primary-foreground',
  todo: 'bg-muted text-muted-foreground',
};

const BAR_STYLES: Record<StepState, string> = {
  complete: 'bg-green-600',
  active: 'bg-primary',
  todo: 'bg-muted',
};

function resolveStepState(
  stepId: WizardStepId,
  activeStepId: WizardStepId,
  completion: WizardCompletion
): StepState {
  if (stepId === activeStepId) return 'active';
  if (completion[stepId]) return 'complete';
  return 'todo';
}

function StepMarker({ state, position }: { state: StepState; position: number }): JSX.Element {
  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
        MARKER_STYLES[state]
      )}
    >
      {state === 'complete' ? <Check className="h-4 w-4" aria-hidden="true" /> : position}
    </span>
  );
}

interface StepButtonProps {
  step: WizardStepDefinition;
  position: number;
  state: StepState;
  onSelect: (stepId: WizardStepId) => void;
}

function StepButton({ step, position, state, onSelect }: StepButtonProps): JSX.Element {
  return (
    <button
      type="button"
      data-state={state}
      aria-current={state === 'active' ? 'step' : undefined}
      onClick={(): void => onSelect(step.id)}
      className="flex flex-col gap-2 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <span className="flex items-center gap-3">
        <StepMarker state={state} position={position} />
        <span className="text-sm font-semibold sm:text-base">{step.title}</span>
      </span>
      <span className="pl-10 text-xs text-muted-foreground sm:text-sm">{step.hint}</span>
      <span className={cn('h-0.5 w-full rounded-full', BAR_STYLES[state])} />
    </button>
  );
}

export function CandidateWizardStepper({
  activeStepId,
  completion,
  onSelect,
}: CandidateWizardStepperProps): JSX.Element {
  return (
    <nav aria-label="Profile steps" className="grid gap-4 sm:grid-cols-2 sm:gap-6">
      {WIZARD_STEPS.map((step, index) => (
        <StepButton
          key={step.id}
          step={step}
          position={index + 1}
          state={resolveStepState(step.id, activeStepId, completion)}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}
